/*
 * Free shipping progress bar.
 *
 * Display only. Subscribes to Dawn's PUB_SUB_EVENTS.cartUpdate and recomputes
 * the bar from the published cart total. When a publisher's payload does not
 * carry a total (e.g. product-form.js publishes the /cart/add.js line item
 * response), it falls back to a READ-ONLY GET of /cart.js. It never writes to
 * the cart.
 */
class FreeShippingBar extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.moneyFormat = this.decodeEntities(this.dataset.moneyFormat) || '${{amount}}';
    this.msgEl = this.querySelector('[data-fs-msg]');
    this.trackEl = this.querySelector('[role="progressbar"]');

    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const total = this.getTotal(event);

      if (typeof total === 'number') {
        this.update(total);
      } else {
        this.refetch();
      }
    });
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  /*
   * cart.js, quick-add-bulk.js, quick-order-list.js and
   * standard-actions-override.js all publish the full cart payload as
   * `cartData`. product-form.js publishes the add-to-cart line item response
   * instead, which has no total — that returns undefined and triggers refetch().
   */
  getTotal(event) {
    const total = event?.cartData?.total_price ?? event?.source?.total_price;

    return typeof total === 'number' ? total : undefined;
  }

  update(totalCents) {
    if (typeof totalCents !== 'number' || Number.isNaN(totalCents)) return;

    const raw = this.threshold > 0 ? Math.min(100, Math.max(0, (totalCents / this.threshold) * 100)) : 0;
    const percent = Math.round(raw * 100) / 100;
    const remainingCents = Math.max(0, this.threshold - totalCents);

    // One property drives both layers: CSS sizes the gradient fill to this
    // percentage and positions the marker at the same boundary.
    this.style.setProperty('--fs-percent', String(percent));

    if (this.trackEl) this.trackEl.setAttribute('aria-valuenow', String(Math.round(percent)));

    if (!this.msgEl) return;

    this.msgEl.textContent =
      remainingCents > 0
        ? (this.dataset.textProgress || '').replace('[remaining]', this.formatMoney(remainingCents))
        : this.dataset.textSuccess || '';
  }

  refetch() {
    fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (cart) this.update(cart.total_price);
      })
      .catch(() => {
        /* Leave the server-rendered value in place. */
      });
  }

  /*
   * Renders cents through the shop's own money format string, handed down from
   * Liquid, so the output matches the `money` filter used on first render.
   */
  formatMoney(cents) {
    const value = cents / 100;

    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, placeholder) => {
      switch (placeholder) {
        case 'amount_no_decimals':
          return this.withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return this.withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return this.withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return this.withDelimiters(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return this.withDelimiters(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return this.withDelimiters(value, 0, ' ', ',');
        default:
          return this.withDelimiters(value, 2, ',', '.');
      }
    });
  }

  // The money format arrives HTML-escaped in a data attribute and can contain
  // entities such as &euro;. Decode once so textContent renders the symbol.
  decodeEntities(value) {
    if (!value) return '';

    const decoder = document.createElement('textarea');
    decoder.innerHTML = value;

    return decoder.value;
  }

  withDelimiters(value, precision, thousands, decimal) {
    const [whole, fraction] = Math.abs(value).toFixed(precision).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  }
}

// The snippet renders in both the drawer and the cart page, so on /cart with a
// drawer cart this script can be requested twice.
if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
